import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FadeUp, MaskLine, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { METRICS, TESTIMONIALS, CAREER_THREAD } from "@/content/site";

const AUDIENCE_PATHS = [
  {
    id: "organizations",
    title: "Organizations",
    headline: "Build a company people want to be part of.",
    items: ["Employee experience", "Customer experience", "Leadership development", "Culture", "Retention", "Communication", "Organizational change", "Team effectiveness", "Systems and workflow", "Fractional leadership", "Strategic consulting"],
    cta: "For Organizations",
    to: "/work-with-jocelyn",
  },
  {
    id: "leaders-teams",
    title: "Leaders & Teams",
    headline: "Better leadership starts with understanding humans.",
    items: ["Leadership identity", "Communication", "Accountability", "Trust", "Conflict", "Team dynamics", "Strengths", "Blind spots", "Ways of being", "Culture", "Performance", "Coaching emerging leaders"],
    cta: "For Leaders & Teams",
    to: "/work-with-jocelyn",
  },
  {
    id: "individuals",
    title: "Individuals",
    headline: "Get clear on who you are — then build from there.",
    items: ["Career transitions", "Leadership", "Confidence", "Communication", "Relationships with work", "Major life changes", "Identity", "Burnout", "Direction", "Personal development"],
    cta: "Coaching",
    to: "/work-with-jocelyn",
  },
  {
    id: "executive-fractional",
    title: "Executive / Fractional Leadership",
    headline: "Sometimes you don't need advice. You need someone who can step in and help lead.",
    items: ["Chief Experience Officer", "Customer experience leadership", "Employee experience leadership", "People & culture", "Organizational development", "Leadership development", "Fractional CX / EX leadership", "Strategic operations", "Consulting", "Facilitation"],
    cta: "Explore My Experience",
    to: "/experience",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28 grid gap-14 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-xs font-sans font-bold uppercase tracking-[0.32em] text-clay"
            data-testid="hero-overline"
          >
            Jocelyn MacDonald
          </motion.p>
          <h1 className="mt-6 font-serif font-medium tracking-tight leading-[1.05] text-ink text-[13.5vw] sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.25}>People are</MaskLine>
            <MaskLine delay={0.38} className="italic text-clay">complicated.</MaskLine>
            <MaskLine delay={0.55} className="mt-4 text-[8.5vw] sm:text-4xl lg:text-5xl text-soot/85">
              Fortunately, they're
            </MaskLine>
            <MaskLine delay={0.68} className="text-[8.5vw] sm:text-4xl lg:text-5xl text-soot/85 italic">
              kind of my thing.
            </MaskLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-mute" data-testid="hero-supporting-copy">
              I've spent more than two decades working at the intersection of people and business — leading
              teams, growing revenue, transforming customer experiences, developing leaders, improving
              organizations, and helping people recognize strengths in themselves they couldn't always see.
              Today, I bring all of it together.
            </p>
            <p className="mt-5 max-w-xl text-sm sm:text-base font-semibold text-ink tracking-wide">
              Coaching. Consulting. Leadership. Customer Experience. Employee Experience. Culture.
            </p>
            <p className="mt-3 font-hand text-2xl text-clay -rotate-1">Different challenges. Same starting point: people.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/work-with-jocelyn"
                data-testid="hero-cta-work-with-me"
                className="inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-sans font-bold uppercase tracking-[0.2em] text-bone transition-all duration-300 hover:bg-claydark hover:gap-3"
              >
                Work With Me <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/experience"
                data-testid="hero-cta-explore-experience"
                className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-7 py-3.5 text-xs font-sans font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-bone"
              >
                Explore My Experience
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div className="relative overflow-hidden rounded-t-[999px] rounded-b-2xl border border-soot/10 shadow-[0_40px_80px_-30px_rgba(22,21,19,0.35)]">
            <motion.img
              src="/images/jocelyn-blazer.jpg"
              alt="Jocelyn MacDonald — confident outdoor portrait in a black and white houndstooth blazer"
              data-testid="hero-portrait"
              style={{ y: imgY }}
              className="aspect-[4/5] w-full object-cover scale-110"
            />
          </div>
          <motion.p
            initial={{ opacity: 0, rotate: 0 }}
            animate={{ opacity: 1, rotate: -6 }}
            transition={{ delay: 1.4, duration: 0.7 }}
            className="absolute -bottom-6 -left-4 sm:-left-10 font-hand text-2xl sm:text-3xl text-ink bg-bone/90 px-3 py-1"
          >
            people person, professionally
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

const BigIdea = () => (
  <section data-testid="big-idea-section" className="py-24 sm:py-32">
    <div className="max-w-4xl mx-auto px-5 sm:px-8">
      <FadeUp>
        <Overline data-testid="big-idea-overline">The Big Idea</Overline>
        <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08] text-ink">
          When people work better,{" "}
          <em className="text-clay">everything</em> works better.
        </h2>
      </FadeUp>
      <FadeUp delay={0.12} className="mt-10 space-y-6 text-base sm:text-lg leading-relaxed text-mute max-w-3xl">
        <p>
          Companies sometimes treat customer experience, employee experience, leadership, culture,
          performance, and personal development as completely different problems.
        </p>
        <p className="font-serif text-2xl sm:text-3xl italic text-ink">They're usually not. They're human problems.</p>
        <p>Behind every customer interaction is a person.</p>
        <p>Behind every team problem is a group of people trying to understand one another.</p>
        <p>
          Behind every leadership challenge is a human being with strengths, fears, habits, blind spots,
          history, and enormous potential.
        </p>
        <p className="text-ink font-semibold">That's where I work.</p>
        <p>
          I help people understand themselves and each other better — then turn that awareness into better
          communication, stronger leadership, healthier teams, happier customers, and better business results.
        </p>
      </FadeUp>
    </div>
  </section>
);

const Metrics = () => (
  <section data-testid="metrics-section" className="bg-cream py-24 sm:py-28 border-y border-soot/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <FadeUp className="max-w-3xl">
        <Overline>Experience &amp; Credibility</Overline>
        <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-ink">
          Human insight is powerful.
          <br />
          <em className="text-clay">Business results are nice too.</em>
        </h2>
      </FadeUp>
      <div className="mt-14 grid gap-px bg-soot/10 border border-soot/10 sm:grid-cols-2 lg:grid-cols-5">
        {METRICS.map((m, i) => (
          <FadeUp key={m.value} delay={0.08 * i} className="bg-bone">
            <div className="h-full p-7 sm:p-8 transition-colors duration-500 hover:bg-blush" data-testid={`metric-card-${i}`}>
              <p className="font-serif text-5xl sm:text-[3.4rem] font-medium text-clay leading-none">{m.value}</p>
              <p className="mt-3 text-xs font-sans font-bold uppercase tracking-[0.2em] text-ink">{m.label}</p>
              <p className="mt-2 text-sm leading-relaxed text-mute">{m.detail}</p>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2}>
        <p className="mt-8 text-sm text-mute max-w-2xl">
          These results came from work spanning customer experience, team leadership, retention, systems,
          communication, sales, and employee development — the numbers matter because of the humans behind them.
        </p>
      </FadeUp>
    </div>
  </section>
);

const CareerThread = () => (
  <section data-testid="career-thread-section" className="py-24 sm:py-32">
    <div className="max-w-5xl mx-auto px-5 sm:px-8">
      <FadeUp className="max-w-3xl">
        <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-ink">
          <em className="text-clay">People</em> have always been at the center of my work.
        </h2>
      </FadeUp>
      <div className="mt-16">
        {CAREER_THREAD.map((item, i) => (
          <FadeUp key={item.step} delay={0.05 * i}>
            <div
              data-testid={`career-step-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group grid gap-2 sm:grid-cols-[90px_240px_1fr] sm:gap-8 items-baseline border-t border-soot/10 py-8 transition-colors duration-500 hover:bg-cream/60"
            >
              <span className="font-serif text-2xl italic text-clay/70">{item.step}</span>
              <h3 className="font-serif text-3xl font-medium text-ink transition-transform duration-500 group-hover:translate-x-2">
                {item.name}
              </h3>
              <p className="text-base leading-relaxed text-mute">{item.desc}</p>
            </div>
          </FadeUp>
        ))}
        <div className="border-t border-soot/10" />
      </div>
      <FadeUp className="mt-16 text-center">
        <p className="text-base sm:text-lg text-mute max-w-xl mx-auto leading-relaxed">
          On paper, these may look like different careers. To me, they've always been the same work.
        </p>
        <p className="mt-6 font-serif text-5xl sm:text-6xl italic font-medium text-ink">
          Understanding <span className="text-clay">people.</span>
        </p>
      </FadeUp>
    </div>
  </section>
);

const AudiencePaths = () => (
  <section data-testid="audience-paths-section" className="bg-cream py-24 sm:py-32 border-y border-soot/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <FadeUp>
        <Overline>Who I Work With</Overline>
        <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-medium tracking-tight text-ink">
          So, how can I help?
        </h2>
      </FadeUp>
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {AUDIENCE_PATHS.map((path, i) => (
          <FadeUp key={path.id} delay={0.08 * i}>
            <div
              data-testid={`audience-card-${path.id}`}
              className="group h-full bg-bone border border-soot/10 p-8 sm:p-10 flex flex-col transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(22,21,19,0.25)]"
            >
              <p className="text-[11px] font-sans font-bold uppercase tracking-[0.25em] text-clay">{path.title}</p>
              <h3 className="mt-4 font-serif text-2xl sm:text-3xl font-medium leading-snug text-ink">{path.headline}</h3>
              <ul className="mt-6 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {path.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-mute">
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-clay" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to={path.to}
                data-testid={`audience-cta-${path.id}`}
                className="mt-auto inline-flex w-fit items-center gap-2 text-[11px] font-sans font-bold uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:text-clay link-sweep"
              >
                {path.cta} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ExperienceConnection = () => (
  <section data-testid="experience-connection-section" className="bg-ink text-[#F5F5F7] py-24 sm:py-32">
    <div className="max-w-4xl mx-auto px-5 sm:px-8">
      <FadeUp>
        <Overline dark>Customer + Employee Experience</Overline>
        <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1]">
          Customer experience and employee experience aren't <em className="text-clay">separate things.</em>
        </h2>
      </FadeUp>
      <FadeUp delay={0.12} className="mt-10 space-y-5 text-base sm:text-lg leading-relaxed text-[#F5F5F7]/70 max-w-3xl">
        <p>Your customers experience your company through your people.</p>
        <p>
          Which means customer experience problems often begin long before a customer ever walks through the
          door, calls support, opens an email, or makes a purchase.
        </p>
        <p>
          They begin with systems. Communication. Leadership. Expectations. Culture. Training. Whether
          employees feel trusted enough to solve problems. Whether they understand why their work matters.
          Whether anyone has actually asked them what they're seeing.
        </p>
        <p>
          Jocelyn's experience spans both sides of this equation — helping organizations create stronger
          experiences for the people they employ and the people they serve.
        </p>
      </FadeUp>
      <FadeUp delay={0.2}>
        <blockquote
          data-testid="cx-ex-pull-quote"
          className="mt-16 border-l-2 border-clay pl-6 sm:pl-10"
        >
          <p className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] italic font-medium leading-[1.2]">
            "Take care of the humans inside the company, and they become much better at taking care of the
            humans outside it."
          </p>
        </blockquote>
      </FadeUp>
    </div>
  </section>
);

const Testimonials = () => {
  const featured = TESTIMONIALS[0];
  const rest = TESTIMONIALS.slice(1);
  return (
    <section data-testid="testimonials-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp>
          <Overline>What People Say</Overline>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-medium tracking-tight text-ink">
            Kind words, <em className="text-clay">real results.</em>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <figure
            data-testid="testimonial-featured"
            className="mt-14 bg-blush border border-clay/20 p-8 sm:p-14 relative"
          >
            <span className="absolute -top-7 left-8 font-serif text-[7rem] leading-none text-clay/25 select-none" aria-hidden="true">
              "
            </span>
            <blockquote className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium italic leading-[1.25] text-ink max-w-4xl">
              "She doesn't just manage people — <span className="text-clay">she invests in them.</span>"
            </blockquote>
            <p className="mt-8 text-base leading-relaxed text-mute max-w-3xl">
              "Jocelyn was an incredible mentor and leader during my time at Sash. Under her guidance, I
              learned to navigate challenges, develop my skills, and grow into my role with confidence. Her
              coaching has set me up for long-term success, both personally and professionally. On top of
              that, her improvements to our systems and workflows made our team more efficient and empowered
              us to deliver a better experience for our customers."
            </p>
            <figcaption className="mt-8 flex items-center gap-4">
              <span className="h-px w-10 bg-clay" />
              <span className="text-xs font-sans font-bold uppercase tracking-[0.22em] text-ink">
                {featured.author} · {featured.role}
              </span>
            </figcaption>
          </figure>
        </FadeUp>
        <div className="mt-8 columns-1 md:columns-2 gap-8">
          {rest.map((t, i) => (
            <FadeUp key={t.author} delay={0.06 * i} className="break-inside-avoid mb-8">
              <figure
                data-testid={`testimonial-card-${t.author.toLowerCase().replace(/[^a-z]/g, "")}`}
                className="bg-bone border border-soot/10 p-8 sm:p-10 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-25px_rgba(22,21,19,0.25)]"
              >
                <blockquote className="text-[15px] leading-relaxed text-mute">"{t.quote}"</blockquote>
                <figcaption className="mt-6 flex items-center gap-4">
                  <span className="h-px w-8 bg-clay" />
                  <span className="text-[11px] font-sans font-bold uppercase tracking-[0.2em] text-ink">
                    {t.author} · {t.role}
                  </span>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

const Philosophy = () => (
  <section data-testid="philosophy-section" className="bg-cream py-24 sm:py-32 border-y border-soot/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:gap-20">
      <FadeUp>
        <Overline>Personal Philosophy</Overline>
        <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-[3.4rem] font-medium tracking-tight leading-[1.08] text-ink">
          People don't need to become <em className="text-clay">someone else.</em>
        </h2>
        <p className="mt-8 text-base sm:text-lg text-mute leading-relaxed">
          Usually, they need help seeing themselves more clearly.
        </p>
        <div className="mt-6 space-y-2 font-serif text-xl sm:text-2xl italic text-ink/85">
          {[
            "Their strengths.",
            "Their patterns.",
            "The things they do naturally that they assume everybody can do.",
            "The strategies that helped them succeed once but might be getting in their way now.",
            "The impact they have on other people.",
            "What matters to them.",
            "What they actually want.",
          ].map((line, i) => (
            <FadeUp key={line} delay={0.05 * i}>
              <p className="border-l-2 border-clay/40 pl-4 py-1">{line}</p>
            </FadeUp>
          ))}
        </div>
      </FadeUp>
      <FadeUp delay={0.15} className="lg:pt-24">
        <div className="relative">
          <img
            src="/images/jocelyn-working.jpg"
            alt="Jocelyn MacDonald working thoughtfully on her laptop in a warm studio setting"
            data-testid="philosophy-portrait"
            className="w-full aspect-[4/5] sm:aspect-[5/6] object-cover rounded-2xl border border-soot/10 grayscale hover:grayscale-0 transition-all duration-700"
          />
          <p className="absolute -bottom-5 right-4 font-hand text-2xl text-ink bg-bone px-3 py-1 rotate-2 shadow-sm">
            see what's already there
          </p>
        </div>
        <p className="mt-12 text-base sm:text-lg leading-relaxed text-mute">
          My job isn't to hand you a personality transplant or a ten-step formula for becoming a better
          human. It's to help you see what's already there.
        </p>
        <p className="mt-4 font-serif text-2xl italic text-ink">Then we figure out what to do with it.</p>
      </FadeUp>
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="final-cta-section" className="bg-ink text-[#F5F5F7] py-28 sm:py-36">
    <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
      <FadeUp>
        <h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05]">
          Let's figure out what's <em className="text-clay">really</em> going on.
        </h2>
      </FadeUp>
      <FadeUp delay={0.12}>
        <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#F5F5F7]/70 max-w-2xl mx-auto">
          Whether you're building a team, navigating change, developing leaders, rethinking customer
          experience, considering your next career move, or simply realizing something in your life isn't
          working anymore —
        </p>
        <p className="mt-4 font-hand text-3xl text-clay">let's talk.</p>
      </FadeUp>
      <FadeUp delay={0.22}>
        <Link
          to="/contact"
          data-testid="final-cta-start-conversation"
          className="mt-10 inline-flex items-center gap-3 rounded-full bg-clay px-9 py-4 text-xs font-sans font-bold uppercase tracking-[0.22em] text-bone transition-all duration-300 hover:bg-claydark hover:gap-4"
        >
          Start a Conversation <ArrowRight className="h-4 w-4" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <BigIdea />
    <Metrics />
    <CareerThread />
    <AudiencePaths />
    <ExperienceConnection />
    <Testimonials />
    <Philosophy />
    <FinalCta />
  </>
);

export default Home;
